import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import './ContentRichness.css';

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function textValue(value) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') return String(first(value.name, value.title, value.label, value.value, '')).trim();
  return '';
}

function listValue(...values) {
  const value = first(...values);
  if (value === undefined || value === null) return [];
  const list = Array.isArray(value) ? value : (typeof value === 'object' ? [value] : String(value).split(/[,|;]/));
  return list.map(textValue).map((entry) => entry.trim()).filter(Boolean);
}

function unique(values, limit = 12) {
  return [...new Set(values.filter(Boolean))].slice(0, limit);
}

function formatDuration(value) {
  const text = textValue(value);
  if (!text) return '';
  if (/^\d+(?:\.\d+)?$/.test(text)) return `${text} min`;
  return text;
}

function formatReleaseDate(value) {
  const text = textValue(value);
  // Release dates are calendar dates: retain the catalog day in every timezone.
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(text);
  if (!match) return text;
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCFullYear() !== Number(year) || date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

function decode(value = '') {
  try { return decodeURIComponent(value); } catch (_) { return value; }
}

function titleById(catalog, id) {
  return catalog?.titles?.find((item) => item.id === id || item.slug === id) || null;
}

function titleFacts(item) {
  const raw = item?.raw || {};
  const writers = unique(listValue(raw.writers, raw.writer, raw.writtenBy, raw.written_by));
  const directors = unique(listValue(raw.directors, raw.director, raw.directedBy, raw.directed_by));
  const creators = unique(listValue(raw.creators, raw.creator, raw.createdBy, raw.created_by));
  const producers = unique(listValue(raw.producers, raw.producer, raw.producedBy, raw.produced_by));
  const cast = unique(listValue(raw.cast, raw.actors, raw.starring, raw.stars), 14);
  const languages = unique(listValue(raw.languages, raw.language, raw.audioLanguages, raw.audio_languages));
  const countries = unique(listValue(raw.countries, raw.country, raw.countryOfOrigin, raw.country_of_origin, raw.productionCountry, raw.production_country));
  const contentRating = textValue(first(raw.contentRating, raw.content_rating, raw.maturityRating, raw.maturity_rating, raw.mpaaRating, raw.mpaa_rating, raw.certificate, raw.rating));
  const releaseDate = formatReleaseDate(first(raw.releaseDate, raw.release_date, raw.premiereDate, raw.premiere_date, raw.originalReleaseDate, raw.original_release_date));
  const studio = textValue(first(raw.productionCompany, raw.production_company, raw.studio, raw.network, raw.label));
  return { writers, directors, creators, producers, cast, languages, countries, contentRating, releaseDate, studio };
}

function episodeFacts(item) {
  const raw = item?.raw || {};
  return {
    directors: unique(listValue(raw.directors, raw.director, raw.directedBy, raw.directed_by)),
    writers: unique(listValue(raw.writers, raw.writer, raw.writtenBy, raw.written_by)),
    cast: unique(listValue(raw.cast, raw.actors, raw.starring, raw.stars), 12),
    releaseDate: formatReleaseDate(first(raw.releaseDate, raw.release_date, raw.airDate, raw.air_date, raw.premiereDate, raw.premiere_date)),
  };
}

function factRows(item, facts, series = false) {
  const rows = [];
  if (item.year) rows.push(['Year', item.year]);
  if (item.duration) rows.push(['Runtime', formatDuration(item.duration)]);
  if (item.genres?.length) rows.push(['Genres', item.genres.join(', ')]);
  if (facts.contentRating) rows.push(['Rating', facts.contentRating]);
  if (facts.releaseDate) rows.push(['Release', facts.releaseDate]);
  if (facts.languages.length) rows.push(['Language', facts.languages.join(', ')]);
  if (facts.countries.length) rows.push(['Country / Region', facts.countries.join(', ')]);
  if (series && item.episodes?.length) {
    const seasons = unique(item.episodes.map((episode) => String(episode.season)), 50);
    rows.push(['Published seasons', seasons.length]);
    rows.push(['Published episodes', item.episodes.length]);
  }
  return rows;
}

function generatedTitleContext(item, facts) {
  const type = item.kind === 'series' ? 'series' : item.short ? 'short film' : 'film';
  const descriptors = [];
  if (item.year) descriptors.push(String(item.year));
  if (item.genres?.length) descriptors.push(item.genres.slice(0, 3).join(' / '));
  if (item.duration && item.kind !== 'series') descriptors.push(formatDuration(item.duration));
  const lead = descriptors.length
    ? `${item.title} is a ${descriptors.join(' • ')} ${type} available through WatchVIM.`
    : `${item.title} is a ${type} available through WatchVIM.`;

  const creditBits = [];
  if (facts.writers.length) creditBits.push(`written by ${facts.writers.join(', ')}`);
  if (facts.directors.length) creditBits.push(`directed by ${facts.directors.join(', ')}`);
  if (facts.creators.length) creditBits.push(`created by ${facts.creators.join(', ')}`);
  if (facts.cast.length) creditBits.push(`featuring ${facts.cast.slice(0, 5).join(', ')}`);
  const credits = creditBits.length ? ` Catalog credits identify the project as ${creditBits.join('; ')}.` : '';

  if (item.kind === 'series') {
    const seasons = unique((item.episodes || []).map((episode) => String(episode.season)), 50).length;
    const episodes = (item.episodes || []).length;
    const guide = episodes ? ` WatchVIM currently lists ${episodes} published episode${episodes === 1 ? '' : 's'} across ${seasons} season${seasons === 1 ? '' : 's'}.` : '';
    return `${lead}${credits}${guide}`;
  }
  return `${lead}${credits}`;
}

function Credits({ facts }) {
  const rows = [
    ['Writer', facts.writers],
    ['Director', facts.directors],
    ['Producer', facts.producers],
    ['Studio', facts.studio ? [facts.studio] : []],
    ['Cast', facts.cast],
  ].filter(([, values]) => values?.length);
  if (!rows.length) return null;
  return (
    <section className="richness-panel" aria-labelledby="richness-credits-title">
      <h2 id="richness-credits-title">Credits</h2>
      <dl className="richness-credits">
        {rows.map(([label, values]) => (
          <div key={label}><dt>{label}{values.length > 1 ? 's' : ''}</dt><dd>{values.join(', ')}</dd></div>
        ))}
      </dl>
    </section>
  );
}

function Details({ rows }) {
  if (!rows.length) return null;
  return (
    <section className="richness-panel" aria-labelledby="richness-details-title">
      <h2 id="richness-details-title">Details</h2>
      <dl className="richness-details">
        {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{String(value)}</dd></div>)}
      </dl>
    </section>
  );
}

function SeriesGuide({ item }) {
  if (!item?.episodes?.length) return null;
  const bySeason = new Map();
  item.episodes.forEach((episode) => {
    const season = String(episode.season || 1);
    bySeason.set(season, (bySeason.get(season) || 0) + 1);
  });
  return (
    <section className="richness-panel" aria-labelledby="richness-guide-title">
      <h2 id="richness-guide-title">Series Guide</h2>
      <p>Browse the published WatchVIM episode catalog by season.</p>
      <div className="richness-season-links">
        {[...bySeason.entries()].map(([season, count]) => (
          <NavLink key={season} to={`/series/${encodeURIComponent(item.id)}/season/${encodeURIComponent(season)}`}>
            Season {season} <span>{count} episode{count === 1 ? '' : 's'}</span>
          </NavLink>
        ))}
      </div>
    </section>
  );
}

function TitleRichness({ item }) {
  const facts = titleFacts(item);
  const rows = factRows(item, facts, item.kind === 'series');
  return (
    <section className="content-richness" aria-label={`More about ${item.title}`} data-content-richness="title">
      <section className="richness-about">
        <span className="eyebrow">About this {item.kind === 'series' ? 'series' : item.short ? 'short' : 'title'}</span>
        <h2>More about {item.title}</h2>
        {item.description ? <p>{item.description}</p> : null}
        <p>{generatedTitleContext(item, facts)}</p>
      </section>
      <div className="richness-grid">
        <Details rows={rows} />
        <Credits facts={facts} />
        {item.kind === 'series' ? <SeriesGuide item={item} /> : null}
      </div>
      {item.genres?.length ? (
        <nav className="richness-genres" aria-label={`${item.title} genres`}>
          <span>Explore similar stories:</span>
          {item.genres.slice(0, 6).map((genre) => <NavLink key={genre} to={`/genres/${encodeURIComponent(genre)}`}>{genre}</NavLink>)}
        </nav>
      ) : null}
    </section>
  );
}

function EpisodeRichness({ series, item }) {
  const facts = episodeFacts(item);
  const runtime = formatDuration(item.duration);
  const rows = [
    ['Series', series.title],
    ['Season', item.season],
    ['Episode', item.episode],
    runtime ? ['Runtime', runtime] : null,
    facts.releaseDate ? ['Release / Air date', facts.releaseDate] : null,
  ].filter(Boolean);
  const credits = [
    ['Director', facts.directors],
    ['Writer', facts.writers],
    ['Cast', facts.cast],
  ].filter(([, values]) => values.length);

  return (
    <section className="content-richness episode-richness" aria-label={`More about ${item.title}`} data-content-richness="episode">
      <section className="richness-about">
        <span className="eyebrow">Episode information</span>
        <h2>About “{item.title}”</h2>
        {item.description ? <p>{item.description}</p> : <p>This is Season {item.season}, Episode {item.episode} of {series.title} on WatchVIM.</p>}
        <p>“{item.title}” is listed as Season {item.season}, Episode {item.episode} in the published WatchVIM guide for {series.title}{runtime ? `, with a listed runtime of ${runtime}` : ''}.</p>
      </section>
      <div className="richness-grid">
        <Details rows={rows} />
        {credits.length ? (
          <section className="richness-panel" aria-labelledby="episode-richness-credits-title">
            <h2 id="episode-richness-credits-title">Episode Credits</h2>
            <dl className="richness-credits">
              {credits.map(([label, values]) => <div key={label}><dt>{label}{values.length > 1 ? 's' : ''}</dt><dd>{values.join(', ')}</dd></div>)}
            </dl>
          </section>
        ) : null}
      </div>
      <NavLink className="richness-back-series" to={`/series/${encodeURIComponent(series.id)}`}>View the full {series.title} series guide</NavLink>
    </section>
  );
}

function routeTarget(pathname) {
  const parts = pathname.split('/').filter(Boolean).map(decode);
  if (parts[0] === 'title' && parts[1] && parts.length === 2) return { kind: 'title', id: parts[1] };
  if (parts[0] === 'series' && parts[1]) return { kind: 'series', id: parts[1] };
  if (parts[0] === 'episode' && parts[1] && parts[2] && parts[3]) return { kind: 'episode', id: parts[1], season: parts[2], episode: parts[3] };
  return null;
}

export function ContentRichnessAddon() {
  const { pathname } = useLocation();
  const { catalog, status } = useCatalog();
  if (status !== 'ready') return null;
  const target = routeTarget(pathname);
  if (!target) return null;
  const item = titleById(catalog, target.id);
  if (!item) return null;

  if (target.kind === 'episode') {
    const episode = item.episodes?.find((entry) => String(entry.season) === String(target.season) && String(entry.episode) === String(target.episode));
    return episode ? <EpisodeRichness series={item} item={episode} /> : null;
  }
  return <TitleRichness item={item} />;
}
